'use client';

import { c as _c } from "react/compiler-runtime";
import './video.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { VideoObject } from 'schema-dts';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { VideoProps } from '@sola/schema/lib/schema.ts';
import { trackClick } from '@sola/tracking/components/datalayer.tsx';
import { cookieConsentVideoURL } from '@sola/tracking/lib/video.ts';
import { getVideoType } from '@sola/utils/video.ts';
import { buildVideoJsonLd } from '../../utils/schema-utils.ts';
import { Image } from '../image/image.tsx';
import { JsonLd } from '../jsonLd/jsonLd.tsx';
import { getPlayerWidth, postIframeCommand, useVideoIframe } from './video.logic.ts';
export const Video = t0 => {
  const $ = _c(66);
  const {
    url,
    poster,
    isBackground,
    autoplay: t1,
    allowPlaying: t2,
    width,
    height,
    thumbnailUrl,
    title,
    uploadDate
  } = t0;
  const autoplay = t1 === undefined ? false : t1;
  const allowPlaying = t2 === undefined ? true : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["skippy"];
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const {
    t
  } = useTranslation(t3);
  const [hasStarted, setHasStarted] = useState(false);
  const [iframeReady, setIframeReady] = useState(false);
  const [playing, setPlaying] = useState(!!isBackground || allowPlaying && autoplay);
  const nativeVideoRef = useRef(null);
  const videoType = getVideoType(url);
  const variableVideoUrl = cookieConsentVideoURL(url);
  const isPortrait = width && height && height > width;
  const shouldRenderSchema = uploadDate && (poster?.url || thumbnailUrl);
  const aspectRatioStr = width && height ? `${width} / ${height}` : "16/9";
  const t4 = !!isBackground;
  const t5 = !!isPortrait;
  let t6;
  if ($[1] !== t4 || $[2] !== t5) {
    t6 = getPlayerWidth(t4, t5);
    $[1] = t4;
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const playerWidth = t6;
  const isProviderVideo = videoType === "vimeo" || videoType === "youtube";
  let t7;
  if ($[4] !== hasStarted) {
    t7 = () => {
      if (!hasStarted) {
        const timeout = setTimeout(() => setHasStarted(true), 100);
        return () => clearTimeout(timeout);
      }
    };
    $[4] = hasStarted;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const handleVideoStart = t7;
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setIframeReady(true);
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const {
    containerRef,
    playerRef: iframeRef
  } = useVideoIframe({
    src: variableVideoUrl,
    videoType,
    isBackground: !!isBackground,
    autoplay: autoplay && allowPlaying,
    onPlay: handleVideoStart,
    onReady: t8
  });
  let t9;
  if ($[7] !== allowPlaying || $[8] !== autoplay || $[9] !== iframeReady || $[10] !== iframeRef || $[11] !== isProviderVideo) {
    t9 = () => {
      if (!iframeReady && !nativeVideoRef.current) {
        return;
      }
      if (!allowPlaying) {
        if (isProviderVideo) {
          postIframeCommand(iframeRef.current, "pause");
        } else {
          nativeVideoRef.current?.pause();
        }
        return;
      }
      if (!autoplay) {
        return;
      }
      let timeout_0 = null;
      if (isProviderVideo) {
        timeout_0 = setTimeout(() => {
          postIframeCommand(iframeRef.current, "play");
        }, 100);
      } else {
        nativeVideoRef.current?.play();
      }
      return () => {
        if (timeout_0) {
          clearTimeout(timeout_0);
        }
      };
    };
    $[7] = allowPlaying;
    $[8] = autoplay;
    $[9] = iframeReady;
    $[10] = iframeRef;
    $[11] = isProviderVideo;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== allowPlaying || $[14] !== autoplay || $[15] !== iframeReady || $[16] !== isProviderVideo) {
    t10 = [allowPlaying, iframeReady, autoplay, isProviderVideo];
    $[13] = allowPlaying;
    $[14] = autoplay;
    $[15] = iframeReady;
    $[16] = isProviderVideo;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  useEffect(t9, t10);
  let t11;
  if ($[18] !== iframeRef || $[19] !== isProviderVideo || $[20] !== playing || $[21] !== t) {
    t11 = () => {
      const next = !playing;
      const label = next ? t("skippy:video.play") : t("skippy:video.pause");
      trackClick(label);
      setPlaying(next);
      if (isProviderVideo) {
        postIframeCommand(iframeRef.current, next ? "play" : "pause");
        return;
      }
      if (next) {
        nativeVideoRef.current?.play();
      } else {
        nativeVideoRef.current?.pause();
      }
    };
    $[18] = iframeRef;
    $[19] = isProviderVideo;
    $[20] = playing;
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const handlePlayPauseToggle = t11;
  const t12 = `Video Video--${videoType}`;
  const t13 = isBackground ? "Video--background" : "Video--inline";
  const t14 = isPortrait ? "h-full" : "w-full";
  let t15;
  if ($[23] !== t12 || $[24] !== t13 || $[25] !== t14) {
    t15 = cn(t12, t13, t14);
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const t16 = shouldRenderSchema && <JsonLd item={{
    "@context": "https://schema.org",
    "@type": "VideoObject",
    ...buildVideoJsonLd({
      name: title || "Video",
      thumbnailUrl: poster?.url || thumbnailUrl,
      contentUrl: variableVideoUrl,
      embedUrl: url,
      description: title,
      uploadDate
    })
  }} />;
  let t17;
  if ($[27] !== aspectRatioStr || $[28] !== containerRef || $[29] !== height || $[30] !== iframeReady || $[31] !== isBackground || $[32] !== isPortrait || $[33] !== isProviderVideo || $[34] !== playerWidth || $[35] !== width) {
    t17 = isProviderVideo && <div ref={containerRef} className={cn("Video__player", {
      "Video__player--portrait": isPortrait,
      "Video__player--landscape": !isPortrait && width,
      "Video__player--default": !width && !height
    })} style={{
      width: playerWidth,
      maxHeight: "100%",
      height: isBackground || isPortrait ? "inherit" : "auto",
      aspectRatio: !isBackground && width && height ? aspectRatioStr : undefined
    }}>{!isBackground && !iframeReady && <div style={{
        position: "absolute",
        inset: 0,
        zIndex: 1
      }}><Skeleton width="100%" height="100%" /></div>}</div>;
    $[27] = aspectRatioStr;
    $[28] = containerRef;
    $[29] = height;
    $[30] = iframeReady;
    $[31] = isBackground;
    $[32] = isPortrait;
    $[33] = isProviderVideo;
    $[34] = playerWidth;
    $[35] = width;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== allowPlaying || $[38] !== aspectRatioStr || $[39] !== autoplay || $[40] !== handleVideoStart || $[41] !== height || $[42] !== isBackground || $[43] !== isPortrait || $[44] !== isProviderVideo || $[45] !== playerWidth || $[46] !== variableVideoUrl || $[47] !== width) {
    t18 = !isProviderVideo && <video ref={nativeVideoRef} src={variableVideoUrl} className={cn("Video__player", {
      "Video__player--portrait": isPortrait,
      "Video__player--landscape": !isPortrait && width,
      "Video__player--default": !width && !height
    })} controls={!isBackground} autoPlay={(!!isBackground || autoplay) && allowPlaying} loop={!!isBackground} muted={!!isBackground} playsInline={true} style={{
      width: playerWidth,
      maxHeight: "100%",
      height: isBackground || isPortrait ? "inherit" : "auto",
      aspectRatio: !isBackground && width && height ? aspectRatioStr : undefined
    }} onPlay={() => {
      setPlaying(true);
      handleVideoStart();
    }} onPause={() => setPlaying(false)} />;
    $[37] = allowPlaying;
    $[38] = aspectRatioStr;
    $[39] = autoplay;
    $[40] = handleVideoStart;
    $[41] = height;
    $[42] = isBackground;
    $[43] = isPortrait;
    $[44] = isProviderVideo;
    $[45] = playerWidth;
    $[46] = variableVideoUrl;
    $[47] = width;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== hasStarted || $[50] !== isBackground || $[51] !== poster) {
    t19 = poster && isBackground && <Image className={cn("Video__poster", {
      "Video__poster--hidden": hasStarted
    })} image={poster} priority={true} />;
    $[49] = hasStarted;
    $[50] = isBackground;
    $[51] = poster;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== handlePlayPauseToggle || $[54] !== hasStarted || $[55] !== isBackground || $[56] !== playing || $[57] !== t) {
    t20 = isBackground && hasStarted && <div className="Video__controls absolute bottom-0 right-0 p-comfy-sm"><ButtonStyle variant="tertiary" size="md"><button type="button" onClick={handlePlayPauseToggle}><ArkIcon iconName={playing ? "pause-circle" : "play-circle"} size="20" />{playing ? t("skippy:video.pause") : t("skippy:video.play")}</button></ButtonStyle></div>;
    $[53] = handlePlayPauseToggle;
    $[54] = hasStarted;
    $[55] = isBackground;
    $[56] = playing;
    $[57] = t;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== t15 || $[60] !== t16 || $[61] !== t17 || $[62] !== t18 || $[63] !== t19 || $[64] !== t20) {
    t21 = <div className={t15}>{t16}{t17}{t18}{t19}{t20}</div>;
    $[59] = t15;
    $[60] = t16;
    $[61] = t17;
    $[62] = t18;
    $[63] = t19;
    $[64] = t20;
    $[65] = t21;
  } else {
    t21 = $[65];
  }
  return t21;
};